'use client';
import React, { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';
import { Loader2, AlertCircle, ChevronDown, ChevronUp, X, Filter } from 'lucide-react';

interface EngagementAdminRow {
  id: string;
  status: string;
  engagement_type: string | null;
  created_at: string;
  closed_at: string | null;
  agreed_rate: number | null;
  identity_revealed: boolean;
  expert_pseudonym: string;
  buyer_org: string;
  has_qa: boolean;
  has_feedback: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-amber-400/15 text-amber-400',
  ACCEPTED: 'bg-primary/10 text-primary',
  IN_PROGRESS: 'bg-secondary/10 text-secondary',
  COMPLETED: 'bg-green-500/10 text-green-400',
  DECLINED: 'bg-red-500/10 text-red-400',
  CANCELLED: 'bg-muted text-muted-foreground',
};

export default function AdminEngagementsTab() {
  const supabase = createClient();
  const [engagements, setEngagements] = useState<EngagementAdminRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [closingId, setClosingId] = useState<string | null>(null);

  useEffect(() => {
    const fetchEngagements = async () => {
      const { data: engs, error: engErr } = await supabase
        .from('engagements')
        .select(`
          id, status, engagement_type, created_at, closed_at, agreed_rate, identity_revealed,
          expert_profiles!engagements_expert_id_fkey (pseudonym),
          buyer_profiles!engagements_buyer_id_fkey (org_name)
        `)
        .order('created_at', { ascending: false });

      if (engErr) { setError(engErr.message); setLoading(false); return; }

      if (engs) {
        const enriched = await Promise.all(
          engs.map(async (eng: any) => {
            const { data: qa } = await supabase
              .from('engagement_qa_responses')
              .select('id')
              .eq('engagement_id', eng.id)
              .maybeSingle();

            const { data: fb } = await supabase
              .from('engagement_feedback')
              .select('id')
              .eq('engagement_id', eng.id)
              .maybeSingle();

            return {
              id: eng.id,
              status: eng.status,
              engagement_type: eng.engagement_type,
              created_at: eng.created_at,
              closed_at: eng.closed_at,
              agreed_rate: eng.agreed_rate,
              identity_revealed: eng.identity_revealed,
              expert_pseudonym: eng.expert_profiles?.pseudonym || '—',
              buyer_org: eng.buyer_profiles?.org_name || 'Individual Buyer',
              has_qa: !!qa,
              has_feedback: !!fb,
            };
          })
        );
        setEngagements(enriched);
      }
      setLoading(false);
    };
    fetchEngagements();
  }, []);

  const handleClose = async (id: string) => {
    setClosingId(id);
    const { error: updateErr } = await supabase
      .from('engagements')
      .update({ status: 'COMPLETED', closed_at: new Date().toISOString(), updated_at: new Date().toISOString() })
      .eq('id', id);

    if (!updateErr) {
      setEngagements((prev) =>
        prev.map((e) => e.id === id ? { ...e, status: 'COMPLETED', closed_at: new Date().toISOString() } : e)
      );
    }
    setClosingId(null);
  };

  const filtered = engagements.filter((e) =>
    statusFilter === 'ALL' || e.status === statusFilter
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={20} className="text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-heading text-lg text-foreground">All Engagements</h2>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-muted-foreground" />
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="appearance-none bg-input border border-border rounded-lg pl-3 pr-8 py-2 text-sm text-foreground outline-none focus:border-primary/50 transition-colors cursor-pointer"
            >
              <option value="ALL">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="ACCEPTED">Accepted</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="COMPLETED">Completed</option>
              <option value="DECLINED">Declined</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
            <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
          <AlertCircle size={14} className="text-red-400" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="surface-elevated p-8 text-center">
          <p className="text-sm text-muted-foreground">No engagements match the current filter.</p>
        </div>
      ) : (
        <div className="surface-elevated overflow-hidden">
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full" role="table">
              <thead>
                <tr className="border-b border-border">
                  {['Expert', 'Buyer', 'Type', 'Status', 'Rate', 'Q&A', 'Feedback', 'Created', 'Actions'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-[10px] font-mono-data uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((eng, idx) => (
                  <React.Fragment key={eng.id}>
                    <tr
                      className={`border-b border-border/50 hover:bg-muted/20 transition-colors cursor-pointer ${idx % 2 === 0 ? '' : 'bg-muted/10'}`}
                      onClick={() => setExpandedId(expandedId === eng.id ? null : eng.id)}
                    >
                      <td className="px-4 py-3">
                        <span className="font-mono-data text-xs font-semibold text-foreground">{eng.expert_pseudonym}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-muted-foreground">{eng.buyer_org}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-muted-foreground">{eng.engagement_type || '—'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`font-mono-data text-[10px] px-2 py-0.5 rounded-full ${STATUS_COLORS[eng.status] || ''}`}>
                          {eng.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono-data text-xs text-foreground">
                          {eng.agreed_rate ? `₹${eng.agreed_rate.toLocaleString('en-IN')}` : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-mono-data ${eng.has_qa ? 'text-secondary' : 'text-muted-foreground'}`}>
                          {eng.has_qa ? '✓' : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-mono-data ${eng.has_feedback ? 'text-secondary' : 'text-muted-foreground'}`}>
                          {eng.has_feedback ? '✓' : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono-data text-xs text-muted-foreground">
                          {new Date(eng.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          {eng.status !== 'COMPLETED' && eng.status !== 'CANCELLED' && eng.status !== 'DECLINED' && (
                            <button
                              onClick={() => handleClose(eng.id)}
                              disabled={closingId === eng.id}
                              className="flex items-center gap-1 px-2.5 py-1 rounded bg-muted text-muted-foreground text-xs font-medium hover:text-foreground hover:bg-muted/80 active:scale-95 transition-all disabled:opacity-60"
                              title="Close Engagement"
                            >
                              {closingId === eng.id ? <Loader2 size={10} className="animate-spin" /> : <X size={10} />}
                              Close
                            </button>
                          )}
                          <button
                            onClick={() => setExpandedId(expandedId === eng.id ? null : eng.id)}
                            className="text-muted-foreground hover:text-foreground transition-colors"
                          >
                            {expandedId === eng.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedId === eng.id && (
                      <tr className="bg-muted/20">
                        <td colSpan={9} className="px-6 py-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                            <div>
                              <p className="text-muted-foreground mb-0.5">Engagement ID</p>
                              <p className="font-mono-data text-foreground break-all">{eng.id}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground mb-0.5">Identity Revealed</p>
                              <p className={`font-mono-data ${eng.identity_revealed ? 'text-secondary' : 'text-muted-foreground'}`}>
                                {eng.identity_revealed ? 'Yes' : 'No'}
                              </p>
                            </div>
                            {eng.closed_at && (
                              <div>
                                <p className="text-muted-foreground mb-0.5">Closed At</p>
                                <p className="font-mono-data text-foreground">
                                  {new Date(eng.closed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
